import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import TablePagination from '../components/TablePagination';

export default function VoucherPage() {
  const { t } = useLang();
  const [vouchers, setVouchers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 0,
    min_purchase: 0, max_discount: 0, valid_from: '', valid_to: '', is_active: true
  });

  const loadVouchers = useCallback(async (page = 1) => {
    try {
      const res = await api.listVouchers(page, 20);
      setVouchers(res.data.data);
      setPagination({ total: res.data.total, page: res.data.page, limit: res.data.limit, total_pages: res.data.total_pages });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadVouchers(); }, [loadVouchers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        discount_value: Number(form.discount_value),
        min_purchase: Number(form.min_purchase),
        max_discount: form.max_discount ? Number(form.max_discount) : null
      };
      if (editing) { await api.updateVoucher(editing.id, data); }
      else { await api.createVoucher(data); }
      toast.success(t('success'));
      closeDialog();
      loadVouchers(pagination.page);
    } catch (err) { toast.error(err?.response?.data?.detail || t('error')); }
  };

  const handleEdit = (v) => {
    setEditing(v);
    setForm({
      code: v.code, discount_type: v.discount_type, discount_value: v.discount_value,
      min_purchase: v.min_purchase || 0, max_discount: v.max_discount || 0,
      valid_from: v.valid_from || '', valid_to: v.valid_to || '', is_active: v.is_active !== false
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false); setEditing(null);
    setForm({ code: '', discount_type: 'percentage', discount_value: 0, min_purchase: 0, max_discount: 0, valid_from: '', valid_to: '', is_active: true });
  };

  const handleDelete = async () => {
    try { await api.deleteVoucher(deleteId); toast.success(t('success')); setDeleteId(null); loadVouchers(pagination.page); }
    catch (err) { toast.error(t('error')); }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val || 0);

  const filtered = vouchers.filter(v => v.code?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="voucher-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('vouchers')}</h1>
          <p className="text-sm text-[#64584F]">{pagination.total} {t('vouchers').toLowerCase()}</p>
        </div>
        <Button onClick={() => { closeDialog(); setDialogOpen(true); }} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="add-voucher-btn">
          <Plus size={16} className="mr-2" /> {t('add')} {t('vouchers')}
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6A2A0]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`${t('search')}...`} className="pl-9 bg-[#F6F5F5] border-transparent focus:bg-white focus:border-[#EB7B44]" data-testid="voucher-search-input" />
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('code')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('discount_type')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">{t('discount_value')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('valid_from')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('valid_to')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('status')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#A6A2A0]">{t('no_data')}</TableCell></TableRow>
            ) : filtered.map(v => (
              <TableRow key={v.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`voucher-row-${v.id}`}>
                <TableCell className="font-medium text-[#202020] font-mono">{v.code}</TableCell>
                <TableCell className="text-[#64584F]">{t(v.discount_type)}</TableCell>
                <TableCell className="text-[#EB7B44] font-semibold hidden sm:table-cell">
                  {v.discount_type === 'percentage' ? `${v.discount_value}%` : formatCurrency(v.discount_value)}
                </TableCell>
                <TableCell className="text-[#64584F] text-sm hidden md:table-cell">{v.valid_from?.split('T')[0] || '-'}</TableCell>
                <TableCell className="text-[#64584F] text-sm hidden md:table-cell">{v.valid_to?.split('T')[0] || '-'}</TableCell>
                <TableCell>
                  <Badge className={`text-xs border ${v.is_active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                    {v.is_active ? t('active') : t('inactive')}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(v)}><Pencil size={15} className="text-[#64584F]" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(v.id)}><Trash2 size={15} className="text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} limit={pagination.limit} onPageChange={(p) => loadVouchers(p)} />
      </Card>

      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-lg">
          <DialogHeader><DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>{editing ? t('edit') : t('add')} {t('vouchers')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('code')}</Label>
                <Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} required className="border-[#CACACC] font-mono" data-testid="voucher-code-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('discount_type')}</Label>
                <Select value={form.discount_type} onValueChange={val => setForm({...form, discount_type: val})}>
                  <SelectTrigger className="border-[#CACACC]" data-testid="voucher-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t('percentage')}</SelectItem>
                    <SelectItem value="nominal">{t('nominal')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('discount_value')}</Label>
                <Input type="number" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} required className="border-[#CACACC]" data-testid="voucher-value-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('min_purchase')}</Label>
                <Input type="number" value={form.min_purchase} onChange={e => setForm({...form, min_purchase: e.target.value})} className="border-[#CACACC]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('max_discount')}</Label>
              <Input type="number" value={form.max_discount} onChange={e => setForm({...form, max_discount: e.target.value})} className="border-[#CACACC]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('valid_from')}</Label>
                <Input type="date" value={form.valid_from} onChange={e => setForm({...form, valid_from: e.target.value})} required className="border-[#CACACC]" data-testid="voucher-from-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('valid_to')}</Label>
                <Input type="date" value={form.valid_to} onChange={e => setForm({...form, valid_to: e.target.value})} required className="border-[#CACACC]" data-testid="voucher-to-input" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={val => setForm({...form, is_active: val})} data-testid="voucher-active-switch" />
              <Label className="text-[#202020]">{t('active')}</Label>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="voucher-save-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">{t('confirm_delete')}</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-voucher-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
